import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { usersApi, type UsersListParams } from '../api/users';
import type {
  AdminPasswordReset, Page, User, UserCreate, UserUpdate, UUID,
} from '../api/types';

export const usersKeys = {
  all:    ['users'] as const,
  list:   (p: UsersListParams) => ['users', 'list', p] as const,
  detail: (id: UUID) => ['users', 'detail', id] as const,
};

export function useUsers(params: UsersListParams) {
  return useQuery<Page<User>, ApiError>({
    queryKey: usersKeys.list(params),
    queryFn: () => usersApi.list(params),
  });
}

export function useUser(id: UUID | undefined) {
  return useQuery<User, ApiError>({
    queryKey: usersKeys.detail(id ?? ''),
    queryFn: () => usersApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation<User, ApiError, UserCreate>({
    mutationFn: usersApi.create,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: usersKeys.all });
      qc.setQueryData(usersKeys.detail(created.id), created);
    },
  });
}

export function useUpdateUser(id: UUID) {
  const qc = useQueryClient();
  return useMutation<User, ApiError, UserUpdate>({
    mutationFn: (payload) => usersApi.update(id, payload),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: usersKeys.all });
      qc.setQueryData(usersKeys.detail(id), updated);
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation<User, ApiError, UUID>({
    mutationFn: usersApi.delete,
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKeys.all }),
  });
}

export function useRestoreUser() {
  const qc = useQueryClient();
  return useMutation<User, ApiError, UUID>({
    mutationFn: usersApi.restore,
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKeys.all }),
  });
}

export function useAdminPasswordReset(id: UUID) {
  const qc = useQueryClient();
  return useMutation<User, ApiError, AdminPasswordReset>({
    mutationFn: (payload) => usersApi.adminPasswordReset(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKeys.all }),
  });
}

/**
 * Phase H0 launch hardening (#108) — admin force-logout. Bumps the
 * target user's session_epoch on the server, which invalidates every
 * cookie they currently hold. Effect is server-side and immediate; the
 * UI re-fetches the user record so the bumped epoch shows up if Pedro
 * is curious about it.
 */
export function useForceLogout(id: UUID) {
  const qc = useQueryClient();
  return useMutation<User, ApiError, void>({
    mutationFn: () => usersApi.forceLogout(id),
    onSuccess: (updated) => {
      qc.setQueryData(usersKeys.detail(id), updated);
      qc.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}
